/* ==========================================================================
   Backup by BioLite — Landing page
   Loaded (deferred) only on page.backup-lp.json via snippets/lp-head.liquid.

   1. Background videos   [data-lp-video]
   2. Vimeo modal          [data-lp-vimeo]
   3. Reveals              [data-lp-animate]
   4. Stacked cards        [data-lp-stack]
   5. Count-up (Module 2)  [data-lp-countup]
   6. Power icons (Mod 7)  .lp-power__icons
   7. App screens (Mod 10) [data-lp-screens]
   8. Init + theme editor

   Motion is skipped entirely for prefers-reduced-motion and in the theme
   editor: content simply shows in its final state.
   ========================================================================== */

(function () {
  'use strict';

  window.lpReady = true;

  var root = document.documentElement;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var hasIO = 'IntersectionObserver' in window;

  function motionOK() {
    return !reducedMotion.matches && !designMode;
  }

  if (!motionOK()) root.classList.remove('lp-anim');


  /* 1. Background videos
     ======================================================================== */

  var videoObserver = null;

  function playVideo(video) {
    video.muted = true; // required for autoplay on iOS
    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function () {
        /* Autoplay blocked (e.g. Low Power Mode). The image stays visible. */
      });
    }
  }

  function loadVideo(video) {
    if (video.dataset.lpLoaded) return;
    video.dataset.lpLoaded = 'true';

    video.querySelectorAll('source[data-src]').forEach(function (source) {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });

    video.addEventListener(
      'playing',
      function () {
        var wrap = video.closest('[data-lp-media]');
        if (wrap) wrap.classList.add('is-playing');
      },
      { once: true }
    );

    video.load();
  }

  function initVideos(scope) {
    var videos = scope.querySelectorAll('video[data-lp-video]');
    if (!videos.length || reducedMotion.matches || saveData) return;

    if (!hasIO) {
      videos.forEach(function (video) {
        loadVideo(video);
        playVideo(video);
      });
      return;
    }

    if (!videoObserver) {
      videoObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target;
            if (entry.isIntersecting) {
              loadVideo(video);
              playVideo(video);
            } else if (!video.paused) {
              video.pause();
            }
          });
        },
        { rootMargin: '200px 0px' }
      );
    }

    videos.forEach(function (video) {
      videoObserver.observe(video);
    });
  }

  function teardownVideos(scope) {
    if (!videoObserver) return;
    scope.querySelectorAll('video[data-lp-video]').forEach(function (video) {
      videoObserver.unobserve(video);
      video.pause();
    });
  }


  /* 2. Vimeo modal
     ======================================================================== */

  var modal = null;
  var modalFrame = null;
  var lastTrigger = null;

  var CLOSE_ICON =
    '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M2 2l12 12M14 2L2 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
    '</svg>';

  /**
   * Accepts:
   *   https://vimeo.com/123456789
   *   https://vimeo.com/123456789/abcdef1234        (unlisted)
   *   https://player.vimeo.com/video/123456789?h=abcdef1234
   */
  function vimeoEmbedUrl(url) {
    var match = String(url).match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-z0-9]+))?/i);
    if (!match) return null;

    var id = match[1];
    var hash = match[2] || null;

    if (!hash) {
      var hParam = String(url).match(/[?&]h=([a-z0-9]+)/i);
      if (hParam) hash = hParam[1];
    }

    var params = ['autoplay=1', 'dnt=1', 'title=0', 'byline=0', 'portrait=0'];
    if (hash) params.push('h=' + hash);

    return 'https://player.vimeo.com/video/' + id + '?' + params.join('&');
  }

  function buildModal() {
    modal = document.createElement('dialog');
    modal.className = 'lp lp-modal';
    modal.setAttribute('aria-label', 'Video');
    modal.innerHTML =
      '<button type="button" class="lp-modal__close" aria-label="Close video">' +
      CLOSE_ICON +
      '</button>' +
      '<div class="lp-modal__frame"></div>';

    modalFrame = modal.querySelector('.lp-modal__frame');

    modal.querySelector('.lp-modal__close').addEventListener('click', function () {
      modal.close();
    });

    // Click on the backdrop (outside the frame) closes the modal.
    modal.addEventListener('click', function (event) {
      if (event.target === modal) modal.close();
    });

    // Remove the player on close so audio stops; return focus to the trigger.
    modal.addEventListener('close', function () {
      modalFrame.innerHTML = '';
      if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
    });

    document.body.appendChild(modal);
  }

  function openVimeo(trigger) {
    var embed = vimeoEmbedUrl(trigger.getAttribute('href') || trigger.dataset.lpVimeoUrl);
    if (!embed) return false;

    if (!modal) buildModal();

    var title = trigger.dataset.lpVimeoTitle || 'Video';
    modal.setAttribute('aria-label', title);

    var iframe = document.createElement('iframe');
    iframe.src = embed;
    iframe.title = title;
    iframe.allow = 'autoplay; fullscreen; picture-in-picture';
    iframe.setAttribute('allowfullscreen', '');

    modalFrame.innerHTML = '';
    modalFrame.appendChild(iframe);

    lastTrigger = trigger;
    modal.showModal();
    return true;
  }

  function supportsDialog() {
    return typeof HTMLDialogElement === 'function' &&
      typeof HTMLDialogElement.prototype.showModal === 'function';
  }

  // One delegated listener covers every module.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-lp-vimeo]');
    if (!trigger || !supportsDialog()) return; // falls back to the Vimeo link
    if (openVimeo(trigger)) event.preventDefault();
  });


  /* 3. Reveals
     Each [data-lp-animate] element fades and rises in when it actually
     enters the viewport (not when its section does), so content that sits
     low in a tall module still animates where you can see it. Elements that
     enter together are staggered in DOM order.

     The motion uses the Web Animations API rather than CSS transitions:
     it runs on the compositor (smooth while scrolling), can't be overridden
     by a section's own transition styles (Modules 13–15), and leaves
     nothing behind once finished.
     ======================================================================== */

  var STAGGER_MS = 90;
  var MAX_STAGGER = 6;
  var REVEAL_MS = 900;
  var EASE = 'cubic-bezier(0.33, 0, 0, 1)';
  var revealObserver = null;
  var sectionObserver = null;
  var revealCallbacks = [];

  // Sections from the theme (Modules 13–15) get reveals added from here,
  // so their own files stay untouched.
  var THEME_SECTION_ITEMS = [
    '.comparison-card-outer',
    '.promo-cards-v2__card',
    '.clp-split-content-v2__media',
    '.clp-split-content-v2__list-item'
  ].join(',');

  function onReveal(fn) {
    revealCallbacks.push(fn);
  }

  // Only the page's own sections (inside <main>), never the header, footer,
  // cart drawer or popups that live elsewhere in <body>.
  function getSectionRoots(scope) {
    var main = document.querySelector('main') || document.getElementById('MainContent');
    if (!main) return [];

    var wrappers;
    if (scope.matches && scope.matches('.shopify-section')) {
      wrappers = main.contains(scope) ? [scope] : [];
    } else {
      wrappers = Array.prototype.slice.call(main.querySelectorAll('.shopify-section')).filter(function (el) {
        return !el.parentElement.closest('.shopify-section');
      });
    }

    return wrappers
      .map(function (wrapper) {
        return wrapper.firstElementChild;
      })
      .filter(Boolean);
  }

  function tagThemeSection(section) {
    if (section.classList.contains('lp')) return;

    var items = section.querySelectorAll(THEME_SECTION_ITEMS);
    var heading = section.querySelector('h1, h2');
    var targets = [];

    if (items.length) {
      if (heading) targets.push(heading.closest('div') || heading);
      targets = targets.concat(Array.prototype.slice.call(items));
    } else {
      // Unknown markup: reveal the section's top-level content.
      var inner = section.children.length === 1 ? section.firstElementChild : section;
      targets = Array.prototype.slice.call(inner.children);
    }

    targets.forEach(function (el) {
      el.setAttribute('data-lp-animate', '');
    });
  }

  function revealElement(el, order) {
    if (el.classList.contains('lp-in')) return;
    el.classList.add('lp-in'); // removes the hidden state (see CSS)

    if (typeof el.animate === 'function') {
      el.animate(
        [
          { opacity: 0, transform: 'translateY(1.5rem)' },
          { opacity: 1, transform: 'none' }
        ],
        {
          duration: REVEAL_MS,
          delay: Math.min(order, MAX_STAGGER) * STAGGER_MS,
          easing: EASE,
          fill: 'backwards'
        }
      );
    }

    revealCallbacks.forEach(function (fn) {
      fn(el);
    });
  }

  function byDomOrder(a, b) {
    return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  }

  function initReveals(scope) {
    var sections = getSectionRoots(scope);
    var animated = root.classList.contains('lp-anim') && hasIO;

    sections.forEach(function (section) {
      if (animated) tagThemeSection(section);
    });

    if (!animated) {
      sections.forEach(function (section) {
        section.classList.add('lp-seen');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          var entering = entries
            .filter(function (entry) { return entry.isIntersecting; })
            .map(function (entry) { return entry.target; })
            .sort(byDomOrder);

          entering.forEach(function (el, i) {
            revealObserver.unobserve(el);
            revealElement(el, i);
          });
        },
        // Fires once the element's top is ~10% up from the bottom of the screen.
        { threshold: 0, rootMargin: '0px 0px -10% 0px' }
      );

      // Section-level: background images settle and the play button pulses.
      sectionObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            sectionObserver.unobserve(entry.target);
            entry.target.classList.add('lp-seen');
          });
        },
        { threshold: 0.15 }
      );
    }

    sections.forEach(function (section) {
      sectionObserver.observe(section);
      section.querySelectorAll('[data-lp-animate]').forEach(function (el) {
        revealObserver.observe(el);
      });
    });
  }


  /* 4. Stacked cards
     A section with [data-lp-stack] stays pinned under the sticky header
     while the next section slides up over it.

     Uses native position: sticky, which the browser handles on the
     compositor, so scrolling stays perfectly smooth (a script-driven
     version lags a frame behind the scroll and jitters, especially on
     phones). Because each Shopify section has its own wrapper, the pair is
     grouped in a container so the pin releases once the next section has
     slid over. Only the dimming is script-driven; it's opacity only, so it
     can't make the page jump.
     ======================================================================== */

  var stacks = [];
  var stackTicking = false;

  // Bottom edge of the theme's sticky header, so pinned sections stop just
  // beneath it. Measured at rest (not every frame).
  function measureHeader() {
    var candidates = document.querySelectorAll(
      '.header__outer-wrapper, .shopify-section-group-header-group, [id*="__header"], header'
    );
    var height = 0;
    for (var i = 0; i < candidates.length; i++) {
      var position = window.getComputedStyle(candidates[i]).position;
      if (position === 'sticky' || position === 'fixed') {
        height = Math.max(0, Math.round(candidates[i].getBoundingClientRect().bottom));
        break;
      }
    }
    root.style.setProperty('--lp-stack-top', height + 'px');
    return height;
  }

  // position: sticky stops working inside an ancestor with overflow: hidden.
  // overflow: clip looks identical but doesn't break sticky.
  function unblockSticky(el) {
    var node = el.parentElement;
    while (node && node !== document.body && node !== root) {
      var style = window.getComputedStyle(node);
      if (style.overflowY === 'hidden' || style.overflowX === 'hidden') {
        node.style.overflow = 'clip';
      }
      node = node.parentElement;
    }
  }

  function updateStacks() {
    stackTicking = false;
    var top = parseFloat(root.style.getPropertyValue('--lp-stack-top')) || 0;

    stacks.forEach(function (stack) {
      var heldHeight = stack.wrapper.offsetHeight;
      var nextTop = stack.next.getBoundingClientRect().top;
      var travel = Math.min(heldHeight, stack.next.offsetHeight) || 1;
      // 0 when the next section starts covering, 1 when it has fully slid in.
      var progress = (top + heldHeight - nextTop) / travel;
      progress = Math.max(0, Math.min(1, progress));
      stack.el.style.setProperty('--lp-stack-p', progress.toFixed(3));
    });
  }

  function requestStackUpdate() {
    if (stackTicking) return;
    stackTicking = true;
    window.requestAnimationFrame(updateStacks);
  }

  function initStacks(scope) {
    if (!motionOK()) return;

    scope.querySelectorAll('[data-lp-stack]').forEach(function (el) {
      var wrapper = el.closest('.shopify-section');
      var next = wrapper && wrapper.nextElementSibling;
      if (!wrapper || !next || wrapper.parentElement.classList.contains('lp-stack-group')) return;

      var group = document.createElement('div');
      group.className = 'lp-stack-group';
      wrapper.parentNode.insertBefore(group, wrapper);
      group.appendChild(wrapper);
      group.appendChild(next);

      wrapper.classList.add('lp-stack-wrapper');
      next.classList.add('lp-stack-next');
      el.classList.add('lp-stack-held');
      unblockSticky(group);

      stacks.push({ el: el, wrapper: wrapper, next: next, group: group });
    });

    if (stacks.length && !initStacks.bound) {
      initStacks.bound = true;
      window.addEventListener('scroll', requestStackUpdate, { passive: true });
      window.addEventListener('resize', function () {
        measureHeader();
        requestStackUpdate();
      });
    }

    if (stacks.length) {
      measureHeader();
      requestStackUpdate();
    }
  }

  function teardownStacks(scope) {
    stacks = stacks.filter(function (stack) {
      if (!scope.contains(stack.el) && scope !== stack.wrapper) return true;
      var group = stack.group;
      if (group.parentNode) {
        group.parentNode.insertBefore(stack.wrapper, group);
        group.parentNode.insertBefore(stack.next, group);
        group.remove();
      }
      stack.el.classList.remove('lp-stack-held');
      stack.wrapper.classList.remove('lp-stack-wrapper');
      stack.next.classList.remove('lp-stack-next');
      return false;
    });
  }


  /* 5. Count-up (Module 2)
     The numbers drop to 0 as the sentence fades in, then count up once the
     sentence is well into view (75% visible), after a short pause.
     Screen readers get the full sentence the whole time.
     ======================================================================== */

  var COUNT_MS = 2000;
  var COUNT_DELAY_MS = 350;

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function wrapNumber(container, value) {
    var pattern = new RegExp('(^|[^\\d.,])(' + value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')(?![\\d])');
    var walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    var node;

    while ((node = walker.nextNode())) {
      var match = node.nodeValue.match(pattern);
      if (!match) continue;

      var start = match.index + match[1].length;
      var numberNode = node.splitText(start);
      numberNode.splitText(match[2].length);

      var span = document.createElement('span');
      span.className = 'lp-count';
      span.style.minWidth = match[2].length + 'ch';
      span.textContent = match[2];
      numberNode.parentNode.replaceChild(span, numberNode);
      return span;
    }
    return null;
  }

  function prepareCountUp(el) {
    if (el.dataset.lpCountReady) return null;
    el.dataset.lpCountReady = 'true';

    var values = (el.getAttribute('data-lp-countup') || '')
      .split(',')
      .map(function (v) { return v.trim(); })
      .filter(Boolean);
    if (!values.length) return null;

    // Keep the real sentence available to assistive tech during the count.
    var srCopy = document.createElement('span');
    srCopy.className = 'lp-visually-hidden';
    srCopy.textContent = el.textContent;
    el.parentNode.insertBefore(srCopy, el.nextSibling);
    el.setAttribute('aria-hidden', 'true');

    var counters = values
      .map(function (value) {
        var span = wrapNumber(el, value);
        if (!span) return null;
        var decimals = (value.split('.')[1] || '').length;
        span.textContent = (0).toFixed(decimals);
        return {
          span: span,
          target: parseFloat(value.replace(/,/g, '')),
          decimals: decimals,
          final: value
        };
      })
      .filter(Boolean);

    return { el: el, srCopy: srCopy, counters: counters };
  }

  function runCountUp(job) {
    var startTime = null;

    function frame(now) {
      if (startTime === null) startTime = now;
      var t = Math.min(1, (now - startTime) / COUNT_MS);
      var eased = easeOutCubic(t);

      job.counters.forEach(function (c) {
        c.span.textContent = t === 1 ? c.final : (c.target * eased).toFixed(c.decimals);
      });

      if (t < 1) {
        window.requestAnimationFrame(frame);
      } else {
        job.el.removeAttribute('aria-hidden');
        job.srCopy.remove();
      }
    }

    window.setTimeout(function () {
      window.requestAnimationFrame(frame);
    }, COUNT_DELAY_MS);
  }

  onReveal(function (el) {
    if (!el.hasAttribute('data-lp-countup')) return;

    var job = prepareCountUp(el);
    if (!job) return;

    var started = false;
    function start() {
      if (started) return;
      started = true;
      runCountUp(job);
    }

    var observer = new IntersectionObserver(
      function (entries) {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        start();
      },
      { threshold: 0.75 }
    );
    observer.observe(el);
  });


  /* 6. Power icons (Module 7)
     Lines draw up from Backup to each appliance, then the appliance lights
     up, one after another. Plays once when the icon row is mostly in view.
     The sequence itself is CSS (.lp-power__icons.lp-seen).
     ======================================================================== */

  function initPower(scope) {
    var rows = scope.querySelectorAll('.lp-power__icons');
    if (!rows.length) return;

    if (!root.classList.contains('lp-anim') || !hasIO) {
      rows.forEach(function (row) { row.classList.add('lp-seen'); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          entry.target.classList.add('lp-seen');
        });
      },
      { threshold: 0.6 }
    );
    rows.forEach(function (row) { observer.observe(row); });
  }


  /* 7. App screens (Module 10)
     With 2+ screens, crossfades every few seconds while in view, with a
     pause button (auto-advancing content needs one for accessibility).
     ======================================================================== */

  var SCREEN_MS = 3200;

  var PAUSE_ICON =
    '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M4 2.5h3v11H4zM9 2.5h3v11H9z" fill="currentColor"/></svg>';
  var PLAY_ICON =
    '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M4.5 2.5v11l9-5.5z" fill="currentColor"/></svg>';

  function initScreens(scope) {
    scope.querySelectorAll('[data-lp-screens]').forEach(function (stack) {
      var screens = Array.prototype.slice.call(stack.querySelectorAll('[data-lp-screen]'));
      if (screens.length < 2 || stack.dataset.lpScreensInit) return;
      stack.dataset.lpScreensInit = 'true';

      var index = 0;
      var timer = null;
      var inView = false;
      var paused = !motionOK();

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'lp-app__toggle';
      stack.appendChild(button);

      function show(i) {
        screens[index].classList.remove('is-active');
        screens[index].setAttribute('aria-hidden', 'true');
        index = i;
        screens[index].classList.add('is-active');
        screens[index].removeAttribute('aria-hidden');
      }

      function stop() {
        window.clearInterval(timer);
        timer = null;
      }

      function start() {
        if (timer || paused || !inView) return;
        timer = window.setInterval(function () {
          show((index + 1) % screens.length);
        }, SCREEN_MS);
      }

      function render() {
        button.innerHTML = paused ? PLAY_ICON : PAUSE_ICON;
        button.setAttribute('aria-label', paused ? 'Play app screens' : 'Pause app screens');
      }

      button.addEventListener('click', function () {
        paused = !paused;
        render();
        if (paused) stop(); else start();
      });

      render();

      if (!hasIO) {
        inView = true;
        start();
        return;
      }

      new IntersectionObserver(
        function (entries) {
          inView = entries[0].isIntersecting;
          if (inView) start(); else stop();
        },
        { threshold: 0.4 }
      ).observe(stack);
    });
  }


  /* 8. Init + theme editor
     ======================================================================== */

  reducedMotion.addEventListener('change', function (event) {
    if (!event.matches) return;
    root.classList.remove('lp-anim');
    document.querySelectorAll('video[data-lp-video]').forEach(function (video) {
      video.pause();
      if (videoObserver) videoObserver.unobserve(video);
    });
    teardownStacks(document);
  });

  function init(scope) {
    initVideos(scope);
    initReveals(scope);
    initStacks(scope);
    initPower(scope);
    initScreens(scope);
  }

  init(document);

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    teardownVideos(event.target);
    teardownStacks(event.target);
  });
})();